import { useState } from 'react';
import type { SegmentRule, Segment, VoteCountsResponse, VoterCounts, VoterDataFieldDto } from '@signalvote/shared';
import { VoterDataFieldKind } from '@signalvote/shared';
import { useVoterRuleOptions, useVoterVoteCounts } from '@/hooks/queries';
import { Button } from '@/components/ui/button';
import { PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { VoterCount } from './VoterCount';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { usePhoneWidth } from '@/hooks';
import { fieldCountsOf, isNarrowed, isYesNo, keptOf, missingPercent, toggledKept, votedOf } from './voterSegment';
import type { FieldCounts } from './voterSegment';

export const popoverClassName = 'w-[min(380px,calc(100vw-24px))] p-4 motion-reduce:animate-none';

export const formatCount = (n: number | undefined) => (n === undefined ? '–' : n.toLocaleString('en-US'));

interface FieldEditorProps {
  field: VoterDataFieldDto;
  /** The segment on screen, which the field's detail is asked about. */
  segment: Segment | null;
  rules: SegmentRule[];
  counts: VoterCounts | undefined;
  /** The list's total, predicted at once when the last counts can say. */
  total: number | undefined;
  provisional: boolean;
  /** Move focus into the popover as it opens, as when it's opened from the keyboard. */
  focusOnOpen?: boolean;
  /** Why no rule can be added, at L2's filter cap: a field without a rule is read-only. */
  capReason?: string | null;
  onKeep: (kept: string[]) => void;
  /** A vote field's choice: voted, didn't vote, or Any (null). */
  onVoted: (voted: boolean | null) => void;
  onDone: () => void;
}

/**
 * One field of the Voter data builder, edited in place. Rendered inside the Popover of whatever
 * opened it, so it grows from that token or chip; at phone width it is a bottom sheet that can be
 * dragged or flicked away instead. `open` mounts the sheet; the Popover shows its own content.
 */
export function VoterDataFieldPopoverContent({ open, ...props }: FieldEditorProps & { open: boolean }) {
  const phone = usePhoneWidth();
  if (phone) {
    return open ? (
      <BottomSheet label={props.field.label} onClose={props.onDone}>
        {(close) => <FieldEditor {...props} onDone={close} />}
      </BottomSheet>
    ) : null;
  }
  return (
    <PopoverContent align="start" className={popoverClassName} onOpenAutoFocus={(e) => props.focusOnOpen || e.preventDefault()}>
      <FieldEditor {...props} />
    </PopoverContent>
  );
}

/**
 * The field's note, whether it filters anyone ("Everyone" or "Only N of M · Reset"), how many
 * voters have no data for it, and its options with the count under each. Multi-value fields are
 * checkboxes that start all checked, with an "only" shortcut and a last box that refuses to
 * uncheck; yes/no fields are Any · one · the other; vote-history fields are Any · Voted · Didn’t vote.
 * Opening it asks for what the list's counts don't hold: a narrowed field's other options, or a
 * vote field's counts.
 */
function FieldEditor({ field, segment, rules, counts, total, provisional, capReason, onKeep, onVoted, onDone }: FieldEditorProps) {
  const [refused, setRefused] = useState(false);
  const kept = keptOf(field, rules);
  const narrowed = isNarrowed(field, rules);
  const locked = !narrowed && !!capReason;
  const vote = field.kind === VoterDataFieldKind.Vote;
  const without = useVoterRuleOptions(segment, field.id, narrowed && !vote).data;
  const votes = useVoterVoteCounts(segment, field.id, vote).data;
  const fieldCounts = fieldCountsOf(field, rules, counts, without);
  const missing = missingPercent(field, fieldCounts);
  const keep = (next: string[]) => {
    setRefused(false);
    onKeep(next);
  };

  return (
    <>
      <p className="text-xs text-muted-foreground">{field.category}</p>
      <h3 className="mt-0.5 flex flex-wrap items-center gap-2 text-base font-semibold">
        {field.label}
        {field.modeled && <ModeledTag />}
      </h3>
      {field.description && <p className="mt-1.5 text-xs text-muted-foreground">{field.description}</p>}

      <p className="mt-3 text-xs font-semibold text-muted-foreground">
        {narrowed ? (
          <>
            <span className="text-primary">
              {isYesNo(field) ? 'Filtering' : `Only ${kept.length} of ${field.options.length}`}
            </span>
            <span aria-hidden="true"> · </span>
            <button
              type="button"
              onClick={() => (vote ? onVoted(null) : keep(field.options.map((o) => o.label)))}
              className="font-semibold text-primary underline decoration-primary/40 underline-offset-2"
            >
              Reset
            </button>
          </>
        ) : (
          (locked && capReason) || 'Everyone'
        )}
      </p>
      {missing !== null && (
        <p className="mt-1 text-xs text-muted-foreground">
          {narrowed ? 'Leaves out' : 'Includes'} the {missing}% with no data
        </p>
      )}

      <fieldset disabled={locked} className={cn('mt-3 min-w-0 disabled:opacity-60', provisional && 'voter-provisional')}>
        {vote ? (
          <VoteOptions field={field} voted={votedOf(field, rules)} base={fieldCounts.base} votes={votes} onVoted={onVoted} />
        ) : isYesNo(field) ? (
          <FlagOptions field={field} kept={kept} narrowed={narrowed} counts={fieldCounts} onKeep={keep} />
        ) : (
          <EnumOptions
            field={field}
            kept={kept}
            narrowed={narrowed}
            counts={fieldCounts}
            onKeep={keep}
            onRefuse={() => setRefused(true)}
          />
        )}
      </fieldset>
      {refused && (
        <p aria-live="polite" className="mt-2 text-xs text-muted-foreground">
          Keep at least one checked. Use “only” to pick a single value.
        </p>
      )}

      <div className="mt-3.5 flex items-center justify-between gap-3 border-t pt-3">
        <span className="text-xs text-muted-foreground">
          {narrowed ? '' : 'Not filtering. '}List:{' '}
          <b className="tabular-nums text-foreground"><VoterCount value={total} /></b>
        </span>
        <Button size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </>
  );
}

function EnumOptions({
  field,
  kept,
  narrowed,
  counts,
  onKeep,
  onRefuse,
}: {
  field: VoterDataFieldDto;
  kept: string[];
  narrowed: boolean;
  counts: FieldCounts;
  onKeep: (kept: string[]) => void;
  onRefuse: () => void;
}) {
  const toggle = (label: string) => {
    const next = toggledKept(kept, label);
    return next ? onKeep(next) : onRefuse();
  };

  return (
    <div>
      {field.options.map((option) => {
        const on = kept.includes(option.label);
        const n = counts.options[option.label];
        const share = counts.base && n !== undefined ? Math.min(1, n / counts.base) : 0;
        return (
          <div key={option.label} className="group flex items-center border-t first:border-t-0">
            <button
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={option.label}
              onClick={() => toggle(option.label)}
              className="grid min-h-10 flex-1 grid-cols-[18px_1fr_72px_60px] items-center gap-2.5 py-1 text-left text-sm active:scale-[0.99] motion-reduce:transform-none"
            >
              <CheckBox on={on} accent={narrowed} />
              <span className={cn(!on && 'text-muted-foreground')}>{option.label}</span>
              <span className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                <span
                  className={cn('block h-full origin-left rounded-full transition-transform duration-500 ease-out motion-reduce:transition-none', narrowed && on ? 'bg-primary' : 'bg-muted-foreground/40')}
                  style={{ transform: `scaleX(${share})` }}
                />
              </span>
              <span className="text-right text-xs tabular-nums text-muted-foreground"><VoterCount value={n} /></span>
            </button>
            <button
              type="button"
              aria-label={`Only ${option.label}`}
              onClick={() => onKeep([option.label])}
              className="ml-1 min-h-6 rounded px-1.5 text-[11px] font-bold text-primary hover:bg-primary/10 [@media(hover:hover)_and_(pointer:fine)]:opacity-0 [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:focus-visible:opacity-100"
            >
              only
            </button>
          </div>
        );
      })}
    </div>
  );
}

export function FlagOptions({
  field,
  kept,
  narrowed,
  counts,
  onKeep,
  note = true,
}: {
  field: VoterDataFieldDto;
  kept: string[];
  narrowed: boolean;
  counts: FieldCounts;
  onKeep: (kept: string[]) => void;
  /** Explain an option that can be counted but not chosen. */
  note?: boolean;
}) {
  const current = narrowed ? kept[0] : null;
  const choice = (key: string, label: string, n: number | undefined, on: boolean, onPick: () => void, disabled = false) => (
    <Choice key={key} label={label} count={n} on={on} accent={narrowed} disabled={disabled} onPick={onPick} />
  );

  const unselectable = field.options.find((o) => !o.selectable);
  return (
    <div>
      <div role="radiogroup" aria-label={field.label} className={choicesClassName}>
        {choice('any', 'Any', counts.base, current === null, () => onKeep(field.options.map((o) => o.label)))}
        {field.options.map((option) =>
          choice(
            option.label,
            option.label,
            counts.options[option.label],
            current === option.label,
            () => onKeep([option.label]),
            !option.selectable,
          ),
        )}
      </div>
      {note && unselectable && (
        <p className="mt-2 text-xs text-muted-foreground">
          “{unselectable.label}” includes voters the voter file has no data on. It’s counted here, but those voters can’t be picked out, so it can’t be chosen on its own.
        </p>
      )}
    </div>
  );
}

/**
 * Any · Voted · Didn’t vote. Each count is measured without the field’s own rule, so flipping it
 * shows what the other side would give; the two always add up to Any.
 */
export function VoteOptions({
  field,
  voted,
  base,
  votes,
  onVoted,
}: {
  field: VoterDataFieldDto;
  voted: boolean | null;
  base: number | undefined;
  votes: VoteCountsResponse | undefined;
  onVoted: (voted: boolean | null) => void;
}) {
  const accent = voted !== null;
  const any = votes?.voted != null && votes.didNot != null ? votes.voted + votes.didNot : base;
  return (
    <div role="radiogroup" aria-label={field.label} className={choicesClassName}>
      <Choice label="Any" count={any} on={voted === null} accent={accent} onPick={() => onVoted(null)} />
      <Choice label="Voted" count={votes?.voted ?? undefined} on={voted === true} accent={accent} onPick={() => onVoted(true)} />
      <Choice label="Didn’t vote" count={votes?.didNot ?? undefined} on={voted === false} accent={accent} onPick={() => onVoted(false)} />
    </div>
  );
}

const choicesClassName = 'grid auto-cols-fr grid-flow-col gap-[3px] rounded-md bg-muted p-[3px]';

/** One segment of an Any · one · the other control, with its count underneath. */
function Choice({
  label,
  count,
  on,
  accent,
  disabled = false,
  onPick,
}: {
  label: string;
  count: number | undefined;
  on: boolean;
  accent: boolean;
  disabled?: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        'flex min-h-[46px] flex-col items-start gap-px rounded-[calc(var(--radius)-4px)] px-3 py-1.5 text-left disabled:cursor-not-allowed disabled:opacity-60',
        on && (accent ? 'bg-primary/10 shadow-[inset_0_0_0_1px] shadow-primary/30' : 'bg-background shadow-sm'),
      )}
    >
      <span className={cn('text-[13px] font-semibold', on ? (accent ? 'text-primary' : 'text-foreground') : 'text-muted-foreground')}>{label}</span>
      <span className="text-[11.5px] tabular-nums text-muted-foreground"><VoterCount value={count} /></span>
    </button>
  );
}

export function CheckBox({ on, accent }: { on: boolean; accent: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-[18px] items-center justify-center rounded-[5px] border-[1.5px] text-background',
        !on && 'border-border bg-background',
        on && (accent ? 'border-primary bg-primary' : 'border-muted-foreground/60 bg-muted-foreground/60'),
      )}
    >
      {on && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5 10 17 19 7" />
        </svg>
      )}
    </span>
  );
}

export function ModeledTag() {
  return (
    <span
      title="A modeled estimate, not a record"
      className="inline-flex items-center rounded-full border border-warning/30 bg-warning/10 px-2 text-[11px] font-semibold leading-[18px] text-warning"
    >
      ≈ modeled
    </span>
  );
}
